// Shared origin-capture helpers for detail-page back breadcrumbs. Both the
// row-link controller (a whole overview row) and the return-origin controller
// (an explicit inner <a>) tag the destination with a `from` query param naming
// the page being left, so that page's back-link returns there — see
// Cup::BackLink::Component / ApplicationHelper#safe_return_path.

// The page the user is leaving, read from the live location so it reflects the
// current filter/scroll state even after Turbo advanced the URL.
export function currentOrigin() {
  return window.location.pathname + window.location.search
}

// Append `origin` to `href` as a `from` param so the destination can link back.
// Two refinements keep the URL clean:
//   - strip any `from` already on the origin, so a detail→detail hop does not
//     nest `?from=…?from=…` (it grows on every hop and double-encodes). The
//     stripped level is not lost: the back-link controller keeps it in the
//     browser session instead — see the chain helpers below.
//   - omit `from` entirely when the origin is the destination's own overview
//     with no filter state — the detail's own fallback already returns there.
export function withReturnOrigin(href, origin) {
  const from = stripFrom(origin)
  if (redundantReturn(href, from)) return href

  const separator = href.includes("?") ? "&" : "?"
  return `${href}${separator}from=${encodeURIComponent(from)}`
}

// Drop the `from` query param from a path+query string, keeping the path and
// every other query param.
export function stripFrom(pathWithQuery) {
  const url = new URL(pathWithQuery, window.location.origin)
  url.searchParams.delete("from")
  return url.pathname + url.search
}

// True when `from` is exactly the destination's parent overview carrying no
// query worth restoring — the collection path of `href` (its path minus the
// trailing `/:id` segment) with no query string. There the detail's own
// fallback already returns to that list, so `from` is redundant.
export function redundantReturn(href, from) {
  const collection = new URL(href, window.location.origin).pathname.replace(/\/[^/]+\/?$/, "")
  return from === collection
}

// The `from` query param of a path+query string: the origin of that page.
export function fromParam(pathWithQuery) {
  return new URL(pathWithQuery, window.location.origin).searchParams.get("from")
}

// ─────────────────────────────────────────────────────────────────────────────
// The deeper levels of the chain.
//
// `from` carries one level only, so a hop from one detail page to another used
// to drop the origin of the page being left, and the way back to a filtered
// overview was lost after a detour. Nesting `?from=…?from=…` would grow the
// address on every click, so the deeper levels live in the browser session
// instead, keyed on the path they belong to: "returning to this path should
// restore that origin". Per tab and per session by design, so a shared link or a
// fresh tab still falls back to the plain overview.
const CHAIN_KEY = "cup:return-chain"

// Levels are evicted oldest-first past this many, so walking back and forth
// between screens can never grow the store without bound.
const MAX_LEVELS = 10

// Mirrors ApplicationHelper#safe_return_path: a same-site relative path only, so
// no level can send the user off the portal. The server re-checks every level it
// is handed; this keeps an unusable one out of the store and off the href.
export function isSafeReturnPath(path) {
  if (typeof path !== "string" || path === "") return false
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return false

  return !/[\u0000-\u001f]/.test(path)
}

// Record that returning to `path` should restore `origin`. The store holds one
// route, so arriving somewhere the route already passed abandons every level
// beyond that point: without that, a cross-link walking back into the screen it
// came from would leave the two pointing at each other and the breadcrumb would
// bounce between them forever. An absent or unsafe origin only truncates, so a
// page reached without one cannot resurrect a stale route either.
export function rememberOrigin(path, origin) {
  const levels = readChain()
  const revisited = levels.findIndex(([key]) => key === path)
  const route = revisited === -1 ? levels : levels.slice(0, revisited)
  if (isSafeReturnPath(origin)) route.push([path, origin])

  writeChain(route.slice(-MAX_LEVELS))
}

export function recallOrigin(path) {
  const levels = readChain()
  const exact = levels.find(([key]) => key === path)
  if (exact) return exact[1]

  // A page can advance its own URL after its level was recorded — a filtered
  // overview re-rendering through Turbo — so the level is keyed on the path it
  // was loaded under. Match on the path without its query as a second pass.
  const wanted = pathnameOf(path)
  const loose = levels.find(([key]) => pathnameOf(key) === wanted)

  return loose ? loose[1] : null
}

function pathnameOf(pathWithQuery) {
  return new URL(pathWithQuery, window.location.origin).pathname
}

// sessionStorage is unavailable in some privacy modes and its contents are user
// input: anything unreadable or misshapen degrades to "no chain remembered".
function readChain() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(CHAIN_KEY) || "[]")
    return Array.isArray(parsed) ? parsed.filter(isLevel) : []
  } catch {
    return []
  }
}

function isLevel(level) {
  return Array.isArray(level) && level.length === 2 &&
    typeof level[0] === "string" && isSafeReturnPath(level[1])
}

function writeChain(levels) {
  try {
    window.sessionStorage.setItem(CHAIN_KEY, JSON.stringify(levels))
  } catch {
    // A full or blocked store just means the chain is not remembered.
  }
}
